import React from 'react';
import {
  Shield,
  Users,
  Settings,
  Eye,
  UserCheck,
  BarChart3,
  Crown,
  Award,
  Briefcase,
  Check,
  X,
  Lock
} from 'lucide-react';
import { RoleDetail, convertLegacyToEnhanced } from '../../types/settings/RoleTypes';
import { RoleLevel, LeadScope, AccessLevel, StructuredPermissions } from '../../types/settings/EnhancedRoleTypes';

interface RoleDetailsViewProps {
  role: RoleDetail | null;
  onClose?: () => void;
}

const RoleDetailsView: React.FC<RoleDetailsViewProps> = ({ role, onClose }) => {
  if (!role) return null;

  // Use actual structured permissions if available, otherwise convert legacy
  const permissions: Partial<StructuredPermissions> = role.structured_permissions || (role.role_permissions ?
    convertLegacyToEnhanced(role).permissions : {});

  // Extract actual role level and lead scope from database
  const roleLevel = role.role_level;
  const leadScope = role.lead_scope;

  // Helper functions for display
  const getRoleLevelLabel = (level?: RoleLevel) => {
    switch (level) {
      case RoleLevel.ADMIN: return 'Administrator';
      case RoleLevel.MANAGER: return 'Manager';
      case RoleLevel.TEAM_LEAD: return 'Team Lead';
      case RoleLevel.SALES_REP: return 'Sales Representative';
      case RoleLevel.VIEW_ONLY: return 'View Only';
      default: return 'Unknown';
    }
  };

  const getLeadScopeLabel = (scope?: LeadScope) => {
    switch (scope) {
      case LeadScope.ALL: return 'All Leads';
      case LeadScope.TEAM: return 'Team Leads';
      case LeadScope.ASSIGNED: return 'Assigned Leads Only';
      case LeadScope.DEPARTMENT: return 'Department Leads';
      case LeadScope.NONE: return 'No Access';
      default: return 'Unknown';
    }
  };

  const getAccessLevelLabel = (level?: AccessLevel | LeadScope | string | boolean) => {
    if (typeof level === 'boolean') {
      return level ? 'Allowed' : 'Denied';
    }
    if (typeof level === 'string') {
      if (Object.values(LeadScope).includes(level as LeadScope)) {
        return getLeadScopeLabel(level as LeadScope);
      }
      switch (level.toLowerCase()) {
        case 'full': return 'Full Access';
        case 'limited': return 'Limited Access';
        case 'none': return 'No Access';
        case 'readonly': return 'Read Only';
        case 'own': return 'Own Records';
        case 'team': return 'Team Records';
        case 'all': return 'All Records';
        default: return level;
      }
    }
    if (!level) return 'Unknown';
    switch (level) {
      case AccessLevel.ALL: return 'All Records';
      case AccessLevel.TEAM: return 'Team Records';
      case AccessLevel.OWN: return 'Own Records';
      case AccessLevel.NONE: return 'No Access';
      default: return 'Unknown';
    }
  };

  const getRoleLevelIcon = (level?: RoleLevel) => {
    switch (level) {
      case RoleLevel.ADMIN: return Crown;
      case RoleLevel.MANAGER: return Award;
      case RoleLevel.TEAM_LEAD: return Users;
      case RoleLevel.SALES_REP: return Briefcase;
      case RoleLevel.VIEW_ONLY: return Eye;
      default: return Shield;
    }
  };

  const LevelIcon = getRoleLevelIcon(roleLevel);

  const PermissionBadge = ({ allowed }: { allowed: boolean | string }) => {
    const isAllowed = allowed === true ||
                     allowed === 'FULL' ||
                     allowed === 'ALL' ||
                     allowed === 'LIMITED' ||
                     allowed === 'own' ||
                     allowed === 'team' ||
                     allowed === 'all' ||
                     (typeof allowed === 'string' && allowed !== 'NONE' && allowed !== 'none' && allowed !== 'false');

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
        isAllowed
          ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
          : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
      }`}>
        {isAllowed ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
        {getAccessLevelLabel(allowed)}
      </span>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Role Overview Section */}
      <div className="flex-shrink-0 p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20">
            <LevelIcon className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="flex-1">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              {role.role_description || 'No description provided'}
            </p>
            <div className="flex items-center gap-4">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
                Level: {getRoleLevelLabel(roleLevel)}
              </span>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
                Lead Scope: {getLeadScopeLabel(leadScope)}
              </span>
              <span className="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400">
                Master Role
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="space-y-6">
          {/* Leads Permissions */}
          {permissions.leads && (
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <Users className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                Lead Management Permissions
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 dark:text-gray-300">Create</span>
                  <PermissionBadge allowed={permissions.leads.create || false} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 dark:text-gray-300">View</span>
                  <PermissionBadge allowed={permissions.leads.view || 'none'} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 dark:text-gray-300">Edit</span>
                  <PermissionBadge allowed={permissions.leads.edit || 'none'} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 dark:text-gray-300">Delete</span>
                  <PermissionBadge allowed={permissions.leads.delete || 'none'} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 dark:text-gray-300">Assign</span>
                  <PermissionBadge allowed={permissions.leads.assign || false} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 dark:text-gray-300">Export</span>
                  <PermissionBadge allowed={permissions.leads.export || false} />
                </div>
                {permissions.leads.import !== undefined && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700 dark:text-gray-300">Import</span>
                    <PermissionBadge allowed={permissions.leads.import} />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Contacts Permissions */}
          {permissions.contacts && (
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <UserCheck className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                Contact Management
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 dark:text-gray-300">Create</span>
                  <PermissionBadge allowed={permissions.contacts.create || false} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 dark:text-gray-300">View</span>
                  <PermissionBadge allowed={permissions.contacts.view || 'none'} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 dark:text-gray-300">Edit</span>
                  <PermissionBadge allowed={permissions.contacts.edit || 'none'} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 dark:text-gray-300">Delete</span>
                  <PermissionBadge allowed={permissions.contacts.delete || 'none'} />
                </div>
              </div>
            </div>
          )}

          {/* Reports Permissions */}
          {permissions.reports && (
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                Reports & Analytics
              </h4>
              <div className="grid grid-cols-2 gap-3">
                {permissions.reports.view_personal !== undefined && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700 dark:text-gray-300">Personal Reports</span>
                    <PermissionBadge allowed={permissions.reports.view_personal} />
                  </div>
                )}
                {permissions.reports.view_team !== undefined && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700 dark:text-gray-300">Team Reports</span>
                    <PermissionBadge allowed={permissions.reports.view_team} />
                  </div>
                )}
                {permissions.reports.view_all !== undefined && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700 dark:text-gray-300">All Reports</span>
                    <PermissionBadge allowed={permissions.reports.view_all} />
                  </div>
                )}
                {permissions.reports.export !== undefined && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700 dark:text-gray-300">Export Reports</span>
                    <PermissionBadge allowed={permissions.reports.export} />
                  </div>
                )}
                {(permissions.reports as any).create !== undefined && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700 dark:text-gray-300">Create Custom</span>
                    <PermissionBadge allowed={(permissions.reports as any).create} />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Administration Permissions */}
          {permissions.administration && (
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <Settings className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                Administration
              </h4>
              <div className="grid grid-cols-2 gap-3">
                {permissions.administration.user_management !== undefined && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700 dark:text-gray-300">User Management</span>
                    <PermissionBadge allowed={permissions.administration.user_management} />
                  </div>
                )}
                {permissions.administration.role_management !== undefined && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700 dark:text-gray-300">Role Management</span>
                    <PermissionBadge allowed={permissions.administration.role_management} />
                  </div>
                )}
                {permissions.administration.form_management !== undefined && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700 dark:text-gray-300">Form Management</span>
                    <PermissionBadge allowed={permissions.administration.form_management} />
                  </div>
                )}
                {permissions.administration.system_settings !== undefined && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700 dark:text-gray-300">System Settings</span>
                    <PermissionBadge allowed={permissions.administration.system_settings} />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Dashboard Permissions */}
          {permissions.dashboard && (
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                Dashboard Access
              </h4>
              <div className="grid grid-cols-2 gap-3">
                {permissions.dashboard.view_personal !== undefined && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700 dark:text-gray-300">Personal Dashboard</span>
                    <PermissionBadge allowed={permissions.dashboard.view_personal} />
                  </div>
                )}
                {permissions.dashboard.view_team !== undefined && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700 dark:text-gray-300">Team Dashboard</span>
                    <PermissionBadge allowed={permissions.dashboard.view_team} />
                  </div>
                )}
                {permissions.dashboard.view_company !== undefined && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700 dark:text-gray-300">Company Dashboard</span>
                    <PermissionBadge allowed={permissions.dashboard.view_company} />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Additional Info */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Lock className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                  Master Role Information
                </h4>
                <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                  This is a system-wide master role with predefined permissions. These roles ensure
                  consistent access control across all clients and cannot be modified. Contact your
                  system administrator if you need different permissions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
        <button
          onClick={onClose}
          className="w-full px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default RoleDetailsView;